import React from "react";

import { Add as AddIcon, Download as DownloadIcon } from "@mui/icons-material";
import JoinInnerSharpIcon from "@mui/icons-material/JoinInnerSharp";
import { Button, Divider, FormControlLabel, Switch } from "@mui/material";

import {
  toggleDeviceAlert,
  togglePatientAlert,
  toggleFetchTableData,
  toggleMissingContactInfo,
  toggleAlertWhenDisconnected,
} from "app/store/patient/slice/patientSlice";
import LoadingModal from "components/common/loading/LoadingModal";
import usePatients from "pages/patients/usePatients";
import { useDispatch, useSelector } from "react-redux";
import { USER_ROLES } from "static/constants";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import DisconnectedMonitorModal from "../modal/DisconnectedMonitorModal";
import ReminderPatientModal from "../modal/ReminderPatientModal";
import { Tooltip } from "@mui/material";
import ColumnToggle from "components/common/table/ColumnToggle";
import PatientListSelection from "components/common/patientListSelection/PatientListSelection";

const PatientTableToolbar = ({ table, filters, sorting }) => {
  const {
    isMerging,
    isDownloading,
    showReminderFilter,
    showMergePatientModal,
    showDisconnectMonitorModal,
    isDownloadReminderPatientList,
    isDownloadingDisconnectedMonitors,
    iSDownloadThirdReminderPatientList,

    downloadPatients,
    setShowReminderFilter,
    setShowMergePatientModal,
    downloadReminderPatients,
    toggleEnrollPatientModal,
    handlePatientRecordMerge,
    downloadDisconnectedMonitor,
    setShowDisconnectMonitorModal,
  } = usePatients(filters, sorting);

  const { currentUser } = useSelector((state) => state.auth);
  const {
    showPatientAlert,
    showDeviceAlert,
    showMissingContactInfo,
    showAlertWhenDisconnected,
  } = useSelector((state) => state.patient);
  const dispatch = useDispatch();

  return (
    <>
      <div className="table-toolbar">
        <div className="d-flex justify-between ">
          <div className="d-flex align-items-center" style={{ gap: 15 }}>
            <GlobalFilterTextField table={table} />
            <Button
              onClick={() => toggleEnrollPatientModal(true)}
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
            >
              Enroll New Patient
            </Button>
            <ColumnToggle table={table} />
          </div>
          {(isDownloading ||
            isDownloadingDisconnectedMonitors ||
            iSDownloadThirdReminderPatientList) && (
            <LoadingModal
              open={
                isDownloading ||
                isDownloadingDisconnectedMonitors ||
                iSDownloadThirdReminderPatientList
              }
              loadingText="Downloading..."
            />
          )}
          <div className="d-flex align-items-center">
            {[USER_ROLES.ADMIN].includes(currentUser?.role) && (
              <Tooltip title="Merge Duplicate Patients." placement="bottom">
                <Button
                  className="mr-2"
                  onClick={() => {
                    setShowMergePatientModal(true);
                  }}
                  variant="contained"
                  color="secondary"
                  startIcon={<JoinInnerSharpIcon />}
                >
                  Merge
                </Button>
              </Tooltip>
            )}

            <Button
              className="mr-2"
              onClick={() => downloadPatients()}
              variant="contained"
              color="secondary"
              disabled={isDownloading}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </div>
        </div>
        <div className="d-flex justify-end mt-2">
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Switch
                onChange={(e, c) => {
                  dispatch(toggleAlertWhenDisconnected(c));
                  dispatch(toggleFetchTableData(true));
                }}
                checked={showAlertWhenDisconnected}
                name={"alertWhenDisconnected"}
              />
            }
            label={"Disconnected Alert"}
            labelPlacement="start"
          />

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className="mr-2"
          />

          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Switch
                onChange={(e, c) => {
                  dispatch(toggleMissingContactInfo(c));
                  dispatch(toggleFetchTableData(true));
                }}
                checked={showMissingContactInfo}
                name={"missingContactInfo"}
              />
            }
            label={"Missing Contact Info"}
            labelPlacement="start"
          />

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className="mr-2"
          />

          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Switch
                onChange={(e, c) => {
                  dispatch(togglePatientAlert(c));
                  dispatch(toggleFetchTableData(true));
                }}
                checked={showPatientAlert}
                name={"patientAlert"}
              />
            }
            label={"Patient Alert"}
            labelPlacement="start"
          />

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className="mr-2"
          />

          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Switch
                onChange={(e, c) => {
                  dispatch(toggleDeviceAlert(c));
                  dispatch(toggleFetchTableData(true));
                }}
                checked={showDeviceAlert}
                name={"deviceAlert"}
              />
            }
            label={"Device Alert"}
            labelPlacement="start"
            className="mr-2"
          />
        </div>
      </div>

      {showDisconnectMonitorModal && (
        <DisconnectedMonitorModal
          isModalOpen={showDisconnectMonitorModal}
          onClose={() => setShowDisconnectMonitorModal(false)}
          onDownload={downloadDisconnectedMonitor}
          userAccounts={currentUser?.accounts}
        />
      )}

      {showReminderFilter && (
        <ReminderPatientModal
          isModalOpen={showReminderFilter}
          onClose={() => setShowReminderFilter(false)}
          disabled={isDownloadReminderPatientList}
          onDownload={downloadReminderPatients}
        />
      )}

      {showMergePatientModal && (
        <PatientListSelection
          successButtonText="Merge"
          cancelButtonText="Cancel"
          showSearchField={true}
          title="Merge Duplicate Patient"
          subTitle="Search for at least 2 patients, then select one as the primary."
          description="Remaining duplicates will be merged into this record and deleted from the system."
          showDrawer={showMergePatientModal}
          handleClose={() => setShowMergePatientModal(false)}
          patientList={[]}
          isProcessing={isMerging}
          handleConfirm={handlePatientRecordMerge}
          confirmModalText={
            <span>Are you sure you want to merge patient records?</span>
          }
        />
      )}
    </>
  );
};

export default PatientTableToolbar;
